import React, { useState, useEffect, useCallback, useMemo } from "react";
import DatePicker, { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { differenceInDays, format, eachDayOfInterval, addMonths, addDays, startOfMonth, endOfMonth } from "date-fns";
import es from "date-fns/locale/es";
import { esAdmin as esAdminCuenta } from "./roles";
import PrecioAdmin, { precioFinal } from "./PrecioAdmin";
import { etiquetaEstado } from "./pagos";
import { hoyUY } from "./fechasUY";
import Swal from "sweetalert2";
import { firestore } from "./firebase";
import { enviarMail } from "./apiEmail"; // <- usa tu Worker
import "./Reservas.css";

function scrollToHash() {
  const hash = window.location.hash;
  if (hash) {
    const elemento = document.querySelector(hash);
    if (elemento) {
      setTimeout(() => {
        elemento.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    }
  }
}

registerLocale("es", es);

function Reservas({ usuario }) {
  const esAdmin = esAdminCuenta(usuario);
  const [fechaInicio, setFechaInicio] = useState(null);
  const [fechaFin, setFechaFin] = useState(null);
  const [casa, setCasa] = useState("");
  const [reservas, setReservas] = useState([]);
  const [bloqueos, setBloqueos] = useState([]);
  const [clienteNombre, setClienteNombre] = useState("");
  const [clienteApellido, setClienteApellido] = useState("");
  // Precio especial (solo admin): normal | acordado | sinCosto
  const [precioTipo, setPrecioTipo] = useState("normal");
  const [precioAcordado, setPrecioAcordado] = useState("");
  const [noches, setNoches] = useState(0);
  const [costoTotal, setCostoTotal] = useState(0);
  const [precioAlta,  setPrecioAlta]  = useState(3000); // ene/feb
  const [precioMedia, setPrecioMedia] = useState(2800); // dic/mar
  const [precioBaja,  setPrecioBaja]  = useState(2500); // resto

  // === 3) CALCULAR COSTO TOTAL (Alta / Media / Baja) ===
  const calcularCostoTotal = useCallback(() => {
    if (!fechaInicio || !fechaFin) { setCostoTotal(0); return; }

    const alta  = Number(precioAlta  ?? 3000); // ene/feb
    const media = Number(precioMedia ?? 2800); // dic/mar
    const baja  = Number(precioBaja  ?? 2500); // resto

    // Noches: desde check-in (incluido) hasta la víspera de check-out (excluida)
    const diasDeEstadia = eachDayOfInterval({
      start: startOfDay(fechaInicio),
      end: addDays(startOfDay(fechaFin), -1),
    });

    const tarifaDelMes = (d) => {
      const m = d.getMonth(); // 0=ene, 1=feb, 2=mar, ..., 11=dic
      if (m === 0 || m === 1) return alta;   // ene/feb
      if (m === 11 || m === 2) return media; // dic/mar
      return baja;
    };

    const total = diasDeEstadia.reduce((acc, d) => acc + tarifaDelMes(d), 0);
    setCostoTotal(total);
  }, [fechaInicio, fechaFin, precioAlta, precioMedia, precioBaja]);

  const [perfilUsuario, setPerfilUsuario] = useState(null);

  // Solicitud de cancelación (usuario)
  const [cancelRequestId, setCancelRequestId] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelOtherText, setCancelOtherText] = useState("");

  // Admin: gestión de cancelaciones
  const [cancelaciones, setCancelaciones] = useState([]);

  // Última solicitud de cancelación del USUARIO por cada reservaId
  const cancelByReservaId = useMemo(() => {
    const out = {};
    for (const c of cancelaciones) {
      if (c.usuarioEmail !== usuario?.email) continue;       // solo las del usuario logueado
      const prev = out[c.reservaId];
      const t  = c.creadoEn?.getTime?.() || 0;
      const pt = prev?.creadoEn?.getTime?.() || 0;
      if (!prev || t >= pt) out[c.reservaId] = c;             // nos quedamos con la más reciente
    }
    return out;
  }, [cancelaciones, usuario?.email]);

  // Helper: "01/09/2025 al 05/09/2025"
  const rango = (ini, fin) =>
    `${format(ini, "dd/MM/yyyy", { locale: es })} al ${format(fin, "dd/MM/yyyy", { locale: es })}`;

  useEffect(() => {
    scrollToHash();
  }, []);

  useEffect(() => {
    if (!usuario?.uid) { setPerfilUsuario(null); return; }
    const unsub = firestore.collection("usuarios").doc(usuario.uid)
      .onSnapshot(s => setPerfilUsuario(s.exists ? s.data() : null));
    return () => unsub && unsub();
  }, [usuario?.uid]);

  useEffect(() => {
    const unsubReservas = firestore.collection("reservas").onSnapshot((snapshot) => {
      const reservasData = snapshot.docs.map((doc) => {
        const d = doc.data();
        return {
          id: doc.id,
          ...d,
          fechaInicio: d.fechaInicio.toDate(),
          fechaFin: d.fechaFin.toDate(),

          // Defaults de pago (para docs antiguos):
          paymentStatus: d.paymentStatus || "pendiente",
          paymentMethod: d.paymentMethod || "",
          paymentNote: d.paymentNote || "",
          paymentConfirmedAt: d.paymentConfirmedAt || null,
          paymentConfirmedBy: d.paymentConfirmedBy || ""
        };
      });

      setReservas(reservasData);
    });

    const unsubBloqueos = firestore.collection("bloqueos").onSnapshot((snapshot) => {
      const bloqueosData = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
        fecha: doc.data().fecha.toDate(),
      }));
      setBloqueos(bloqueosData);
    });

    const unsubPrecios = firestore
      .collection("config")
      .doc("precios")
      .onSnapshot((doc) => {
        if (doc.exists) {
          const data = doc.data();
          setPrecioAlta (data.precioAlta  ?? 3000);
          setPrecioMedia(data.precioMedia ?? 2800);
          setPrecioBaja (data.precioBaja  ?? 2500);
        }
      });

    // Admin: escuchar cancelaciones
    const unsubCancel = firestore.collection("cancelaciones").onSnapshot((snapshot) => {
      const data = snapshot.docs.map((d) => {
        const raw = d.data();
        return {
          id: d.id,
          ...raw,
          creadoEn: raw.creadoEn?.toDate ? raw.creadoEn.toDate() : raw.creadoEn || new Date(0),
          resueltaEn: raw.resueltaEn?.toDate ? raw.resueltaEn.toDate() : raw.resueltaEn || null,
        };
      });
      setCancelaciones(data);
    });

    return () => {
      unsubReservas();
      unsubBloqueos();
      unsubPrecios();
      unsubCancel();
    };
  }, []);

  useEffect(() => {
    if (fechaInicio && fechaFin) {
      const dias = differenceInDays(fechaFin, fechaInicio);
      if (dias > 0) {
        setNoches(dias);
        calcularCostoTotal();
      } else {
        setNoches(0);
        setCostoTotal(0);
      }
    } else {
      setNoches(0);
      setCostoTotal(0);
    }
  }, [fechaInicio, fechaFin, calcularCostoTotal]);

  const esFechaReservada = (fecha) => {
    return reservas.some(
      (reserva) =>
        reserva.casa === casa &&
        fecha >= reserva.fechaInicio &&
        fecha <= reserva.fechaFin
    );
  };

  const startOfDay = (d) => {
    const x = new Date(d);
    x.setHours(0, 0, 0, 0);
    return x;
  };

  const sameDay = (a, b) => {
    if (!a || !b) return false;
    return startOfDay(a).getTime() === startOfDay(b).getTime();
  };

  // Devuelve la reserva que cubre ese día (o undefined)
  const reservaQueCubre = (fecha) => {
    const f0 = startOfDay(fecha);
    return reservas.find(
      (r) =>
        r.casa === casa &&
        f0 >= startOfDay(r.fechaInicio) &&
        f0 <= startOfDay(r.fechaFin)
    );
  };

  const obtenerClaseDia = (date) => {
    const hoy = startOfDay(hoyUY());
    const f0  = startOfDay(date);
    if (f0 < hoy) return "";

    // bloqueos mandan
    const isBlocked = bloqueos.some(b => b.casa === casa && sameDay(b.fecha, f0));
    if (isBlocked) return "bloqueado";

    // estado de reserva (si existe)
    const r = reservaQueCubre(f0);
    const classes = [];

    if (r) {
      const esInicio = sameDay(f0, r.fechaInicio);
      const esFin    = sameDay(f0, r.fechaFin);
      if (esInicio && esFin) classes.push("reserva--middle");
      else if (esInicio)     classes.push("reserva--start");
      else if (esFin)        classes.push("reserva--end");
      else                   classes.push("reserva--middle");
    } else {
      classes.push("fecha-disponible");
    }

    // overlay de selección del usuario (borde azul)
    const isUserStart = fechaInicio && sameDay(f0, fechaInicio);
    const isUserEnd   = fechaFin    && sameDay(f0, fechaFin);
    const isBetween   =
      fechaInicio && fechaFin &&
      f0 > startOfDay(fechaInicio) && f0 < startOfDay(fechaFin);

    if (isUserStart || isUserEnd || isBetween) {
      classes.push("seleccionado"); // 👈 agrega el borde sin tocar el fondo
    }

    return classes.join(" ");
  };

  const fechasBloqueadas = (inicio, fin) => {
    const dias = eachDayOfInterval({ start: inicio, end: fin });
    return dias.some((fecha) =>
      bloqueos.some(
        (bloqueo) =>
          bloqueo.casa === casa &&
          bloqueo.fecha.toDateString() === fecha.toDateString()
      )
    );
  };

  // ---------- MAILS: CONFIRMACIÓN DE RESERVA ----------
  const enviarMailReserva = async (nombre, correo, casa, fechaInicio, fechaFin, costoTotal) => {
    const rango = `${format(fechaInicio, "dd/MM/yyyy")} al ${format(fechaFin, "dd/MM/yyyy")}`;

    const subjUser  = `Reserva confirmada · ${casa} · ${rango}`.replace(/\s+/g, " ").trim();
    const subjAdmin = `Nueva reserva · ${nombre} · ${casa} · ${rango}`.replace(/\s+/g, " ").trim();

    // Usuario
    try {
      await enviarMail({
        to: correo,
        subject: subjUser,
        html: `
          <p>Hola ${nombre},</p>
          <p>Tu reserva en <b>${casa}</b> fue confirmada:</p>
          <ul>
            <li>📅 ${rango}</li>
            <li>💰 UYU ${costoTotal.toLocaleString("es-UY")}</li>
          </ul>
          <p>⏰ <b>Check-in: 12:00</b></p>
          <p>🏁 <b>Check-out: 9:30</b></p>
          <p>💳 Recordá pagar tu estadía <b>antes</b> de la fecha de ingreso y enviar el comprobante de pago.</p>
          <p><a href="https://www.reservascuchi.com/info">Información útil</a></p>
          <hr style="border:none;border-top:1px solid #eee;margin:16px 0" />
          <p style="margin:0 0 4px 0">📲 Enviar comprobante de pago a <b>Ricardo Bachmann</b> al:</p>
          <p style="margin:0">
            <a href="https://wa.me/59899503445" target="_blank" rel="noopener">099 503 445</a>
          </p>
        `,
        text:
          `Hola ${nombre}\n` +
          `Tu reserva en ${casa} fue confirmada.\n` +
          `Fechas: ${rango}\n` +
          `Total: UYU ${costoTotal.toLocaleString("es-UY")}\n` +
          `Check-in: 12:00\n` +
          `Check-out: 9:30\n` +
          `Recordá pagar tu estadía antes de la fecha de ingreso y enviar el comprobante de pago.\n` +
          `Información útil: https://www.reservascuchi.com/info\n` +
          `Enviar comprobante de pago a Ricardo Bachmann al: 099 503 445\n`
      });
    } catch (e) {
      console.error("Error mail usuario:", e);
    }

    // Copia admin
    try {
      await enviarMail({
        to: "reservas.cuchillaalta@gmail.com",
        subject: subjAdmin,
        html: `
          <p>Se confirmó una nueva reserva.</p>
          <ul>
            <li>Cliente: ${nombre} (${correo})</li>
            <li>Casa: ${casa}</li>
            <li>Fecha: ${rango}</li>
            <li>Costo: UYU ${costoTotal.toLocaleString("es-UY")}</li>
          </ul>
          <hr style="border:none;border-top:1px solid #eee;margin:16px 0" />
          <p style="margin:0">
            📞 Contacto p/huéspedes: <a href="https://wa.me/59898925827" target="_blank" rel="noopener">098 925 827</a>
          </p>
        `,
        text:
          `Nueva reserva\n` +
          `Cliente: ${nombre} (${correo})\n` +
          `Casa: ${casa}\n` +
          `Fecha: ${rango}\n` +
          `Costo: UYU ${costoTotal.toLocaleString("es-UY")}\n` +
          `Contacto (WhatsApp): 098 925 827\n`
      });
    } catch (e) {
      console.error("Error mail admin:", e);
    }
  };

  const totalFinal = esAdmin ? precioFinal(precioTipo, precioAcordado, costoTotal) : costoTotal;

  const manejarReserva = () => {
    if (esAdmin && precioTipo === "acordado" && precioAcordado === "") {
      Swal.fire("Error", "Ingresá el monto acordado.", "error");
      return;
    }
    if (!usuario || !usuario.email) {
      Swal.fire("Error", "Debes iniciar sesión para hacer una reserva.", "error");
      return;
    }

    if (!fechaInicio || !fechaFin || !casa) {
      Swal.fire("Error", "Completa todos los datos.", "error");
      return;
    }

    const duracion = differenceInDays(fechaFin, fechaInicio);
    const hoy0 = startOfDay(hoyUY());
    const f0   = startOfDay(fechaInicio);

    const esTemporadaAlta = f0.getMonth() === 0 || f0.getMonth() === 1;
    const dentroDe30Dias  = differenceInDays(f0, hoy0) <= 30;

    // REGLA NUEVA: se libera EL MES COMPLETO contado desde el 1° del mes actual
    //   - miembros: 3 meses desde el 1°
    //   - no miembros: 1 mes desde el 1°
    const mesesPermitidos = esMiembro ? 3 : 1;
    const baseMes        = startOfMonth(hoy0);                    // ej. 2025-09-01 00:00
    const mesHabilitado  = addMonths(baseMes, mesesPermitidos);   // ej. +3 => 2025-12-01
    const limite         = endOfMonth(mesHabilitado);             // ej. 2025-12-31 23:59
    // La reserva es válida si el INGRESO (fechaInicio) cae hasta el último día de ese mes habilitado
    if (!esAdmin && f0.getTime() > limite.getTime()) {
      Swal.fire(
        "Error",
        `Solo se puede reservar con hasta ${mesesPermitidos} mes${mesesPermitidos > 1 ? "es" : ""} de anticipación.`,
        "error"
      );
      return;
    }

    if (!esAdmin && esTemporadaAlta && !dentroDe30Dias) {
      if (duracion < 5 || duracion > 10) {
        Swal.fire(
          "Error",
          "En enero y febrero solo se puede reservar entre 5 y 10 noches, excepto si faltan menos de 30 días para el ingreso.",
          "error"
        );
        return;
      }
    }

    if (duracion < 1 || duracion > 14) {
      Swal.fire("Error", "La estadía debe ser de 1 a 14 noches.", "error");
      return;
    }

    if (!esAdmin && fechasBloqueadas(fechaInicio, fechaFin)) {
      Swal.fire("Error", "Alguna de las fechas seleccionadas está bloqueada.", "error");
      return;
    }

    const overlap = reservas.some(
      (reserva) =>
        reserva.casa === casa &&
        fechaInicio < reserva.fechaFin &&
        fechaFin > reserva.fechaInicio
    );

    if (overlap) {
      Swal.fire("Error", "Las fechas se superponen con otra reserva.", "error");
      return;
    }

    const nombreCliente = esAdmin
      ? `${clienteNombre} ${clienteApellido}`.trim()
      : `${usuario?.nombre || ""} ${usuario?.apellido || ""}`.trim() || usuario.email;

    firestore
      .collection("reservas")
      .add({
        nombre: nombreCliente,
        email: usuario.email,
        casa,
        fechaInicio,
        fechaFin,

        // Monto cotizado al momento de reservar (queda fijo aunque cambien los precios)
        total: totalFinal,
        precioTipo: esAdmin ? precioTipo : "normal",

        // === Campos de pago (nuevo) ===
        paymentStatus: esAdmin && precioTipo === "sinCosto" ? "sinCosto" : "pendiente", // pendiente | parcial | confirmado | sinCosto
        paymentMethod: "",                // "efectivo" | "transferencia" | "abitab" | "otro"
        paymentNote: "",                  // texto libre (ej: "Itaú ref 1234")
        paymentConfirmedAt: null,         // fecha cuando se confirme
        paymentConfirmedBy: "",           // email admin que confirma

        // === NUEVO: quién creó y desde dónde ===
        creadoPorUid: usuario ? usuario.uid : null,
        creadoPorEmail: usuario ? usuario.email : null,
        origen: esAdmin ? "admin-reservar" : "web-usuario",
      })

      .then(() => {
        enviarMailReserva(nombreCliente, usuario.email, casa, fechaInicio, fechaFin, totalFinal);
        Swal.fire({
          title: "Éxito",
          html:
            "Reserva realizada con éxito.<br><br>" +
            "📩 Revisá tu correo, te enviamos la confirmación.<br>" +
            (esAdmin && precioTipo === "sinCosto"
              ? "💳 Reserva <b>sin costo</b>."
              : "💳 Estado de pago: <b>Pendiente</b> (el administrador lo confirmará)."),
          icon: "success",
        });
        setFechaInicio(null);
        setFechaFin(null);
        setCasa("");
        setClienteNombre("");
        setClienteApellido("");
        setPrecioTipo("normal");
        setPrecioAcordado("");
      })
      .catch(() => {
        Swal.fire("Error", "No se pudo guardar la reserva.", "error");
      });

  };

  // ---------- BORRADO DIRECTO (solo admin) ----------
  const cancelarReserva = (id) => {
    Swal.fire({
      title: "¿Cancelar esta reserva?",
      text: "Esta acción no se puede deshacer.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, cancelar",
      cancelButtonText: "No",
    }).then((result) => {
      if (result.isConfirmed) {
        firestore
          .collection("reservas")
          .doc(id)
          .delete()
          .then(() => Swal.fire("Cancelada", "Reserva cancelada.", "info"))
          .catch(() => Swal.fire("Error", "No se pudo cancelar.", "error"));
      }
    });
  };

  // ---------- SOLICITUD DE CANCELACIÓN (usuario) ----------
  const handleCancelSubmit = async (e, reserva) => {
    e.preventDefault();

    // 1) Guarda en Firestore la solicitud
    let docRef;
    try {
      docRef = await firestore.collection("cancelaciones").add({
        reservaId: reserva.id,
        usuarioEmail: usuario.email,
        usuarioNombre: usuario?.nombre || "",
        usuarioApellido: usuario?.apellido || "",
        motivo: cancelReason,
        detalleOtro: cancelReason === "otro" ? cancelOtherText : "",
        estado: "pendiente",
        creadoEn: new Date(),
      });

      // 1.1) Marca la reserva como "cancelación solicitada"
      await firestore.collection("reservas").doc(reserva.id).update({
        cancelRequested: true,
        cancelRequestId: docRef.id,
      });
    } catch (err) {
      console.error("Error guardando solicitud:", err);
      Swal.fire("Error", "No se pudo enviar la solicitud.", "error");
      return;
    }

    // 2) Aviso al admin por mail (igual que tenías)
    try {
      const asuntoAdminCancel = `Solicitud de cancelación · ${(usuario?.nombre || "") + " " + (usuario?.apellido || "")} · ${reserva.casa} · ${rango(reserva.fechaInicio, reserva.fechaFin)}`
        .replace(/\s+/g, " ")
        .trim();

      await enviarMail({
        to: "reservas.cuchillaalta@gmail.com",
        subject: asuntoAdminCancel,
        html: `
          <p>Usuario: ${usuario?.nombre || ""} ${usuario?.apellido || ""} (${usuario.email})</p>
          <p>Reserva ID: ${reserva.id}</p>
          <p>Motivo: ${cancelReason === "otro" ? cancelOtherText : cancelReason}</p>
          <p>Fechas: ${rango(reserva.fechaInicio, reserva.fechaFin)}</p>
        `,
        text:
          `Solicitud de cancelación\n` +
          `Usuario: ${(usuario?.nombre || "") + " " + (usuario?.apellido || "")} (${usuario.email})\n` +
          `Reserva ID: ${reserva.id}\n` +
          `Motivo: ${cancelReason === "otro" ? cancelOtherText : cancelReason}\n` +
          `Fechas: ${rango(reserva.fechaInicio, reserva.fechaFin)}\n`,
      });

      Swal.fire("Solicitud enviada", "El administrador la revisará en breve.", "success");
    } catch (err) {
      console.error("Error enviando correo al admin:", err);
      Swal.fire("Guardado", "La solicitud fue guardada, pero no se pudo notificar por correo.", "warning");
    }

    // 3) Reset pequeño
    setCancelRequestId(null);
    setCancelReason("");
    setCancelOtherText("");
  };

  // ---------- ADMIN: aprobar / rechazar cancelaciones ----------
  const findReservaById = (id) => reservas.find((r) => r.id === id);

  const rechazarCancelacion = async (sol) => {
    const confirmar = await Swal.fire({
      title: "Rechazar cancelación",
      text: "¿Seguro? Se notificará al usuario.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, rechazar",
      cancelButtonText: "No",
    });
    if (!confirmar.isConfirmed) return;

    try {
      await firestore.collection("cancelaciones").doc(sol.id).update({
        estado: "rechazada",
        resueltaEn: new Date(),
      });

      // Notificar al usuario
      try {
        await enviarMail({
          to: sol.usuarioEmail,
          subject: "Cancelación rechazada",
          html: `
            <p>Hola,</p>
            <p>Tu solicitud de cancelación fue <b>rechazada</b>.</p>
            <p>Si necesitás cambiar la fecha, elegí "Quiero cambiar la fecha" al solicitar.</p>
            <p>¿Dudas o pagos? Escribinos por WhatsApp: <a href="https://wa.me/59898925827" target="_blank" rel="noopener">098 925 827</a></p>
            <p>Saludos,<br/>Reservas Cuchi</p>
          `,
          text:
            `Tu solicitud de cancelación fue rechazada.\n` +
            `Si necesitás cambiar la fecha, elegí "Quiero cambiar la fecha" al solicitar.\n` +
            `Contacto (WhatsApp): 098 925 827\n`
        });
      } catch (e) {
        console.error("No se pudo notificar al usuario:", e);
      }

      Swal.fire("Listo", "Solicitud rechazada.", "success");
    } catch (e) {
      console.error(e);
      Swal.fire("Error", "No se pudo rechazar la solicitud.", "error");
    }
  };

  // Filtrado y orden (más nuevas arriba)
    const reservasMostradas = useMemo(() => {
    if (esAdmin) {
      // Admin en la sección "Reservar": mostrar SOLO las que creó él aquí
      return reservas.filter(
        (r) =>
          r.creadoPorEmail === usuario?.email ||
          r.creadoPorUid === usuario?.uid ||
          r.origen === "admin-reservar"
      );
    }
    // Usuario común: sus propias reservas
    return reservas.filter((r) => r.email === usuario?.email);
  }, [reservas, esAdmin, usuario?.email, usuario?.uid]);

  const reservasOrdenadas = useMemo(
    () => [...reservasMostradas].sort((a, b) => b.fechaInicio.getTime() - a.fechaInicio.getTime()),
    [reservasMostradas]
  );

  // --- Membresía (3 meses) robusta ---
  const normalize = (s) =>
    (s ?? "")
      .toString()
      .normalize("NFD")
      .replace(/\p{Diacritic}/gu, "") // quita acentos
      .replace(/\./g, "")             // quita puntos (Igl.)
      .replace(/\s+/g, " ")
      .trim()
      .toLowerCase();

  // toma de perfil > usuario > vacío
  const congSource = perfilUsuario?.congregacion ?? usuario?.congregacion ?? "";
  const cong = normalize(congSource);

  // Palabras clave del grupo (soporta "ombu" u "ombú")
  const groupKeys = ["costa", "delta", "gartental", "ombu"];

  // Miembro si NO dice “ninguna” y contiene alguna de las 4
  const esMiembro = !cong.includes("ninguna") && groupKeys.some(k => cong.includes(k));

  // (debug opcional)
  useEffect(() => {
    console.log("[DEBUG] cong src:", congSource, "=>", cong, "esMiembro:", esMiembro);
  }, [congSource, cong, esMiembro]);

  // Admin: ordenar cancelaciones (pendientes primero, luego por fecha desc)
  const cancelacionesOrdenadas = [...cancelaciones].sort((a, b) => {
    if (a.estado !== b.estado) {
      if (a.estado === "pendiente") return -1;
      if (b.estado === "pendiente") return 1;
    }
    return (b.creadoEn?.getTime?.() || 0) - (a.creadoEn?.getTime?.() || 0);
  });

    // --- Componente pequeño para mostrar el estado de pago ---
    function BadgePago({ status }) {
      const st = ["pendiente", "parcial", "confirmado", "sinCosto"].includes(status) ? status : "pendiente";
      return <span className={`badge badge--${st}`}>{etiquetaEstado(st)}</span>;
    }

  return (
    <div className="container">
      <div className="seccion-reserva">
        <h1>Reservar una Casa</h1>
        {usuario && (
          <div className="formulario">
            {esAdmin && (
              <>
                <div className="form-group">
                  <label>Nombre Cliente:</label>
                  <input value={clienteNombre} onChange={(e) => setClienteNombre(e.target.value)} />
                </div>
                <div className="form-group">
                  <label>Apellido Cliente:</label>
                  <input value={clienteApellido} onChange={(e) => setClienteApellido(e.target.value)} />
                </div>
              </>
            )}

            <div className="form-group">
              <label>Casa:</label>
              <select value={casa} onChange={(e) => setCasa(e.target.value)}>
                <option value="">-- Selecciona una casa --</option>
                <option value="Casa 1">Casa 1</option>
                <option value="Casa 2">Casa 2</option>
                <option value="Casa 3">Casa 3</option>
                <option value="Casa 4">Casa 4</option>
              </select>
            </div>

            {casa && (
              <div className="form-group">
                <label>Seleccionar Rango de Fechas:</label>
                <div className="leyenda-calendario">
                  <div style={{ display: "flex", gap: "15px", marginBottom: "5px" }}>
                    <span><span className="color-circulo reserva"></span> Ocupado</span>
                    <span><span className="color-circulo bloqueado"></span> Bloqueado</span>
                  </div>
                  <div style={{ display: "flex", gap: "15px" }}>
                    <span><span className="color-circulo fecha-disponible"></span> Disponible</span>
                    <span><span className="color-circulo seleccionado"></span> Seleccionado</span>
                  </div>
                </div>

                {/* Mensaje sobre anticipación */}
                <div style={{ marginBottom: "10px", color: "#555", fontSize: "14px" }}>
                  {esAdmin ? (
                    "Como administrador, podés reservar sin límite de anticipación."
                  ) : esMiembro ? (
                    "Podés reservar hasta 3 meses antes de la fecha de ingreso."
                  ) : (
                    "Podés reservar hasta 1 mes antes de la fecha de ingreso."
                  )}
                </div>

                {/* Aclaración de medias celdas (check-in / check-out) */}
                <div style={{ marginTop: "-6px", marginBottom: "10px", color: "#555", fontSize: "14px" }}>
                  El día de check-in / check-out se muestran con la casilla a la mitad.
                </div>

                <div style={{ marginTop: "-6px", marginBottom: "10px", color: "#555", fontSize: "14px" }}>
                  <b>Tocá en el calendario de abajo para seleccionar fecha inicio y fecha fin.</b>
                </div>

                <DatePicker
                  selected={fechaInicio}
                  onChange={(dates) => {
                    const [start, end] = dates;
                    setFechaInicio(start);
                    setFechaFin(end);
                  }}
                  startDate={fechaInicio}
                  endDate={fechaFin}
                  selectsRange
                  inline
                  locale="es"
                  dayClassName={obtenerClaseDia}
                  calendarClassName="cal-reservas"
                  openToDate={null}
                />
              </div>
            )}

            <div className="form-group">
              <label>Fechas seleccionadas:</label>
              <div>
                {fechaInicio ? format(fechaInicio, "dd/MM/yyyy") : "—"}{" → "}
                {fechaFin ? format(fechaFin, "dd/MM/yyyy") : "—"}
              </div>
            </div>

            <div
                style={{
                  marginTop: 10,
                  marginBottom: 6,
                  background: "#f7f7f7",
                  borderRadius: 8,
                  padding: "10px 12px",
                  fontSize: 14,
                  lineHeight: 1.4,
                }}
              >
                <div style={{ fontWeight: 600, marginBottom: 6 }}>Precios por Casa por noche</div>
                <div>Alta (enero/febrero): <b>UYU {Number(precioAlta).toLocaleString("es-UY")}</b></div>
                <div>Media (diciembre/marzo): <b>UYU {Number(precioMedia).toLocaleString("es-UY")}</b></div>
                <div>Baja (resto del año): <b>UYU {Number(precioBaja).toLocaleString("es-UY")}</b></div>
              </div>

            {esAdmin && noches > 0 && (
              <PrecioAdmin
                tipo={precioTipo}
                setTipo={setPrecioTipo}
                monto={precioAcordado}
                setMonto={setPrecioAcordado}
                normal={costoTotal}
              />
            )}

            {noches > 0 && (
              <div className="bloque-resumen-confirmar">
                <div className="resumen-reserva">
                  <span>🛏 {noches} noche{noches > 1 ? "s" : ""}</span>
                  <span>
                    💰 {esAdmin && precioTipo === "sinCosto"
                      ? "Sin costo"
                      : `UYU ${totalFinal.toLocaleString("es-UY")}`}
                  </span>
                </div>
                <button className="confirmar-btn" onClick={manejarReserva}>
                  Confirmar Reserva
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="seccion-mis-reservas">
        <h2 id="misreservas">{esAdmin ? "Reservas hechas por el Administrador" : "Mis Reservas"}</h2>
        <ul>
          {reservasOrdenadas.map((reserva) => {
            // Tomamos la última solicitud del USUARIO (si existe) para esta reserva
            const csol = cancelByReservaId[reserva.id];
            // Usa el estado de la última solicitud del usuario;
            // si aún no llegó la snapshot, cae al flag del doc de la reserva.
            const estadoCancel = csol?.estado || (reserva.cancelRequested ? "pendiente" : null);

            // === Normalizamos fechas a comienzo del día para comparar por día calendario ===
            const hoy = hoyUY(); hoy.setHours(0, 0, 0, 0);
            const fin = new Date(reserva.fechaFin); fin.setHours(0, 0, 0, 0);
            const pasada = fin < hoy;             // 👈 condición “reserva pasada”
            const reservaVigente = !pasada;       // usamos lo mismo para habilitar el botón

            return (
              <li
                key={reserva.id}
                className={`reserva-item ${pasada ? "reserva-pasada" : ""}`}
              >
                {/* Cabecera de la reserva (texto + badges + botones) */}
                <div
                  className="reserva-head"
                  style={{
                    display: "flex",
                    gap: 8,
                    alignItems: "center",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    width: "100%",
                  }}
                >
                  <span
                    className="reserva-text"
                    style={pasada ? { textDecoration: "line-through" } : undefined} // fallback visual
                  >
                    {reserva.nombre} reservó la <b>{reserva.casa}</b> del{" "}
                    {reserva.fechaInicio.toLocaleDateString()} al{" "}
                    {reserva.fechaFin.toLocaleDateString()}
                  </span>

                  <div
                    className="reserva-actions"
                    style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}
                  >
                    {pasada && <span className="badge badge--finalizada">Finalizada</span>}

                    <BadgePago status={reserva.paymentStatus || "pendiente"} />

                    {/* Badges de cancelación según el estado de la última solicitud del usuario */}
                    {estadoCancel === "pendiente" && (
                      <span className="badge badge--c-pendiente">Cancelación pendiente</span>
                    )}
                    {estadoCancel === "rechazada" && (
                      <span className="badge badge--c-rechazada">Cancelación rechazada</span>
                    )}

                    {esAdmin ? (
                      <button className="cancelar-btn" onClick={() => cancelarReserva(reserva.id)}>
                        Cancelar
                      </button>
                    ) : (
                      reservaVigente && (
                        <button
                          onClick={() => setCancelRequestId(reserva.id)}
                          disabled={estadoCancel === "pendiente"}
                          title={estadoCancel === "pendiente" ? "Ya hay una solicitud pendiente" : ""}
                        >
                          Solicitar cancelación
                        </button>
                      )
                    )}
                  </div>
                </div>

                {/* Formulario de solicitud:
                    - Solo se muestra si abriste esta reserva
                    - Y NO hay una solicitud 'pendiente' del usuario */}
                {cancelRequestId === reserva.id && estadoCancel !== "pendiente" && (
                  <form onSubmit={(e) => handleCancelSubmit(e, reserva)} className="cancel-solicitud-form">
                    <label className="cs-label">Motivo de cancelación:</label>
                    <select
                      className="cs-input"
                      value={cancelReason}
                      onChange={(e) => setCancelReason(e.target.value)}
                      required
                    >
                      <option value="">-- Elegí un motivo --</option>
                      <option value="planes">Cambio de planes personales</option>
                      <option value="salud">Problemas de salud o emergencia</option>
                      <option value="clima">Condiciones climáticas adversas</option>
                      <option value="fecha">Quiero cambiar la fecha</option>
                      <option value="otro">Otro motivo</option>
                    </select>

                    {cancelReason === "otro" && (
                      <>
                        <label className="cs-label" htmlFor={`cs-otro-${reserva.id}`}>
                          Detallá:
                        </label>
                        <input
                          id={`cs-otro-${reserva.id}`}
                          type="text"
                          className="cs-input"
                          value={cancelOtherText}
                          onChange={(e) => setCancelOtherText(e.target.value)}
                          required
                        />
                      </>
                    )}

                    <div className="cancel-solicitud-actions">
                      <button type="submit">Enviar solicitud</button>
                      <button
                        type="button"
                        onClick={() => {
                          setCancelRequestId(null);
                          setCancelReason("");
                          setCancelOtherText("");
                        }}
                      >
                        Cerrar
                      </button>
                    </div>
                  </form>
                )}
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}

export default Reservas;